import type { AuthUser } from "../../types/authUser";
import { getTierLimits, isUnlimitedTierLimit } from "../../utils/tierLimits";

// Converts "UDOBO-PRE-PRIMARY-SCHOOL-DEMO-ACCOUNT" to "Udobo Pre Primary School Demo Account"
export const formatOrganizationName = (orgId?: string): string => {
  if (!orgId) return "";
  return orgId
    .toLowerCase()
    .replace(/-/g, " ")
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/** "SCHOOL" as "School" */
export const formatBusinessType = (type?: string): string => {
  if (!type) return "";
  const trimmed = type.trim();
  if (trimmed.length === 0) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/** A Plan as Account shows it: its name and what it includes. */
export type Plan = { name: string; description: string };

const formatLimit = (n: number) => (isUnlimitedTierLimit(n) ? "unlimited" : `up to ${n}`);

/** The Plan for a tier, as Account's Organization details show it. */
export function planOf(tier: string | undefined): Plan {
  const key = tier || "0";
  const limits = getTierLimits(key);
  const caps = `${formatLimit(limits.maxSubcircles)} circles and ${formatLimit(limits.maxTeamMembers)} team members`;
  switch (key) {
    case "1":
      return {
        name: "Growth",
        // Templates and Drive are on every plan, Community included, so they're never
        // named here: naming them would make upgrading look like it unlocks them.
        description: `Growth plan: notices, events, circles, and team within higher caps. Limits: ${caps}.`,
      };
    case "2":
      return {
        name: "Premium",
        description: `Premium plan: Ad Center, AI worksheets, family profiles, and priority support. Limits: ${caps}.`,
      };
    case "3":
    case "4":
    case "5":
      return {
        name: "Premium",
        description: `Premium plan (unlimited): all features with no practical caps on circles or team. Limits: ${caps}.`,
      };
    default:
      return {
        name: "Community",
        description: `Community plan: notices, events, circles, and team within caps. Limits: ${caps}.`,
      };
  }
}

/** The tier with no caps; nothing is above it. */
const TOP_TIER = 3;

/** The Plan above this one, or nothing on the top Plan. */
export function nextPlanOf(tier: string | undefined): Plan | undefined {
  const current = Number(tier || "0");
  return current >= TOP_TIER ? undefined : planOf(String(current + 1));
}

/** Account's Organization details, as its labels show them; anything not set is left out. */
export type OrganizationDetails = {
  /** "Organization" for a Team member; an admin's Account says "Circle Name" */
  nameLabel: "Organization" | "Circle Name";
  name: string;
  plan: string;
  billing: string;
  description?: string;
  type?: string;
  phoneNumber?: string;
  website?: string;
  organizationId: string;
};

const textOrNothing = (value: unknown) => (typeof value === "string" && value.trim() ? value : undefined);

/**
 * A description's first sentence, with its own full stop; "…" in its place when there's
 * more after it, so the label that follows it on Account never doubles the full stop.
 */
function firstSentenceOf(text: string): string {
  const trimmed = text.trim();
  const end = trimmed.search(/[.!?](?:\s|$)/);
  if (end === -1) return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  const rest = trimmed.slice(end + 1).trim();
  return rest ? `${trimmed.slice(0, end)}…` : trimmed.slice(0, end + 1);
}

const descriptionOf = (schoolDescription: unknown): string | undefined => {
  const text = textOrNothing(schoolDescription);
  return text ? firstSentenceOf(text) : undefined;
};

/** The name Account shows for the Organization. */
export const organizationNameOf = (user: { userType?: string; orgId?: string; schoolName?: string }) =>
  user.userType === "team" ? formatOrganizationName(user.orgId) : user.schoolName || "";

export function organizationDetailsOf(user: AuthUser): OrganizationDetails {
  return {
    nameLabel: user.userType === "team" ? "Organization" : "Circle Name",
    name: organizationNameOf(user),
    plan: planOf(user.tier).name,
    billing: user.sponsored === true ? "Sponsored (not paying)" : "Not sponsored",
    description: descriptionOf(user.schoolDescription),
    type: formatBusinessType(user.schoolType) || undefined,
    phoneNumber: textOrNothing(user.phoneNumber),
    website: textOrNothing(user.website),
    organizationId: organizationIdOf(user),
  };
}

/** Organization ID (OID), as Account shows it. */
export function organizationIdOf(user: Pick<AuthUser, "orgId" | "schoolId" | "PK">): string {
  return (
    user.orgId ||
    user.schoolId ||
    (typeof user.PK === "string" ? user.PK.replace(/^ORG#/, "").replace(/^SCHOOL#/, "") : "")
  );
}
